(ns client-portal.client.oasys.sessions.data
  (:require [shared.core.reactive :as re]
            [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.core.data :as core.d]
            [client-portal.client.products.data :as products.d]
            [client-portal.client.router :as cp.router]
            [client-portal.client.voice-insights.data :as vi.d]
            [client-portal.client.voice-insights.sessions.data :as vi.sessions.d]
            [shared.core.time :as time]
            [shared.core.util :as util]))

(def root-path [:oasys :sessions])
(def list-path (conj root-path :list))
(def count-path (conj root-path :count))
(def selected-conversation-path (conj root-path :selected-conversation))
(def header-height-path (conj root-path :header-height))
(def table-columns-path (conj root-path :table-columns))
;; The Voice Insights transcript list (reused in the panel) reads its search from here.
(def search-transcript-path (conj vi.sessions.d/root-path :search-transcript))
(def page-size 30)
(def panel-key :oasys/session-panel)

(defn base-query-args
  "The header's concept and date window, plus the applied filters. The OASYS header has
   no Most Recent or Live timeframe, so one carried over from Voice Insights falls back
   to the default window."
  [db args]
  (let [timeframe (core.d/selected-timeframe db)
        db (cond-> db
                   (contains? #{core.d/most-recent-timeframe-value -2} timeframe)
                   (assoc-in core.d/timeframe-path core.d/default-timeframe-value))
        filters (vi.d/filter-data-from-route db)]
    (cond-> (merge (core.d/timeframe-date-params db)
                   {:concept-id (concepts.d/current-concept-id db)}
                   (products.d/project-params db ["oasys"])
                   args)
            (not-empty filters) (assoc :filters filters))))

(defn page-path [page]
  (conj list-path page))

(defn current-max-page [db]
  (some->> (get-in db list-path)
           (util/when-not-loading)
           (keys)
           (apply max)))

(defn list-loading? [db]
  (let [list-data (get-in db list-path)]
    (or (util/loading? list-data) (some-> list-data (get 0) (util/loading?)))))

(re/reg-sub :oasys/sessions-list-loading?- list-loading?)

(defn next-page-loading? [db]
  (let [page (current-max-page db)]
    (and (pos? page) (util/loading? (get-in db (page-path page))))))

(re/reg-sub :oasys/sessions-next-page-loading?- next-page-loading?)

(defn has-more? [db]
  (some->> (get (util/when-not-loading (get-in db list-path)) (current-max-page db))
           (util/when-not-loading)
           (count)
           (<= page-size)))

(re/reg-sub :oasys/sessions-has-more?- has-more?)

(defn next-page [db]
  (some-> (current-max-page db) (inc)))

(re/reg-sub :oasys/sessions-next-page- next-page)

(def resolution-styles
  {"RESOLVED" {:icon-type :check-circle
               :color "#4AC53F"}
   "PARTIALLY_RESOLVED" {:icon-type :do-not-disturb-on
                         :color "#F99602"}
   "UNRESOLVED" {:icon-type :cancel
                 :color "#FF8080"}})

(defn resolution-style [resolution-status]
  (get resolution-styles resolution-status {:icon-type :help
                                            :color "#919296"}))

(defn message-count [{:keys [end-user-utterance-count amelia-utterance-count agent-utterance-count]}]
  (->> [end-user-utterance-count amelia-utterance-count agent-utterance-count]
       (keep identity)
       (reduce + 0)))

(defn transf-conversation [conversation]
  (update conversation :conversation-ts #(time/reformat-date-str % "M/d/yy h:mm A")))

(re/reg-auth0-loader
  :oasys/load-sessions
  (fn [[[_ {:keys [page]}]] _]
    (page-path (or page 0)))
  (fn [db [_ {:keys [page]} _]]
    (when (and (not (next-page-loading? db))
               (not (list-loading? db))
               (concepts.d/current-concept-id db))
      (let [last-loaded (some-> (util/when-not-loading (get-in db list-path))
                                (get (or (dec (or page 0)) 0))
                                (last))]
        {:method :post
         :url "/api/oasys/conversations/list"
         :json-params (cond-> (base-query-args db {:limit page-size})
                              last-loaded
                              (assoc :before (:conversation-created-micros last-loaded)
                                     :before-conversation-id (:conversation-id last-loaded)))})))
  :transf (fn [resp]
            (or (some->> resp
                         :body
                         (util/->kebab-case-keywords)
                         (mapv transf-conversation))
                []))
  :error-transf (fn [] []))

(re/reg-sub
  :oasys/sessions-list-
  (fn [db]
    (let [current-list (get-in db list-path)]
      (if (nil? current-list)
        (re/dispatch-load [:oasys/load-sessions [nil {:page 0}]])
        current-list))))

(defn flattened-list [list-data]
  (some->> (util/when-not-loading list-data)
           (into (sorted-map))
           (remove #(util/loading? (last %)))
           (mapcat last)
           (vec)))

(re/reg-sub
  :oasys/sessions-flattened-
  :<- [:oasys/sessions-list-]
  flattened-list)

(re/reg-auth0-loader
  :oasys/load-sessions-count
  count-path
  (fn [db]
    (when (concepts.d/current-concept-id db)
      {:method :post
       :url "/api/oasys/conversations/count"
       :json-params (base-query-args db {})}))
  :transf #(-> % :body (util/->kebab-case-keywords) :count)
  :error-transf (fn [] 0))

(re/reg-auth0-load-sub
  :oasys/sessions-count-
  count-path
  :oasys/load-sessions-count)

(re/reg-event-db
  :oasys/reload-sessions re/middleware
  (fn [db]
    (-> db
        (assoc-in list-path nil)
        (assoc-in count-path nil))))

(defn conversation-path [{:keys [conversation-id]}]
  (conj root-path :conversation-by-id conversation-id))

(re/reg-auth0-loader
  :oasys/load-conversation
  (fn [[[_ args]]]
    (conversation-path args))
  (fn [db [_ {:keys [conversation-id conversation-date]}]]
    (when conversation-id
      {:method :get
       :url "/api/oasys/conversation"
       :query-params (-> {:conversation-id conversation-id
                          :conversation-date conversation-date
                          :concept-id (concepts.d/current-concept-id db)}
                         (util/remove-nil-from-map)
                         (merge (products.d/project-params db ["oasys"])))}))
  :transf #(or (some-> % :body (util/->kebab-case-keywords) (transf-conversation)) [])
  :error-transf (fn [] []))

(re/reg-auth0-load-sub
  :oasys/conversation-
  (fn [_ args]
    (conversation-path args))
  :oasys/load-conversation)

(re/reg-sub
  :oasys/selected-session-
  (fn [db]
    (get-in db selected-conversation-path)))

;; `conversation-date` rides along so the lookup reads one partition; a searched id
;; arrives without it.
(defn conversation->conv-qp [{:keys [conversation-id conversation-date]}]
  (try
    (some-> (when conversation-id
              {:conversation-id conversation-id
               :conversation-date conversation-date})
            (util/remove-nil-from-map)
            (util/map->json-string)
            (util/base-64-encode))
    (catch :default _
      nil)))

(defn go-with-merged-conv [conv]
  (re/dispatch [:router/go-with-merged-query-params {:conv (conversation->conv-qp conv)}]))

(re/reg-event-db
  :oasys/toggle-session-panel re/middleware
  (fn [db [args]]
    (let [currently-open? (= (get-in db [:layout :active-panel-right 0]) panel-key)
          open? (:open? args true)
          cur-convo (get-in db selected-conversation-path)
          args (cond-> (dissoc args :open?)
                       (and (= (:conversation-id cur-convo) (:conversation-id args))
                            (:convo-idx cur-convo)
                            (not (:convo-idx args)))
                       (assoc :convo-idx (:convo-idx cur-convo)))]
      (cond
        (not open?) (re/dispatch [:layout/hide-panel-right])
        (not currently-open?) (re/dispatch [:layout/show-panel-right [panel-key]]))
      (go-with-merged-conv (when open? args))
      (assoc-in db selected-conversation-path (when open? args)))))

;; The URL no longer names a session — drop the selection without routing, since the URL
;; is already where it should be.
(re/reg-event-db
  :oasys/clear-selected-session re/middleware
  (fn [db]
    (if (get-in db selected-conversation-path)
      (do
        (when (= (get-in db [:layout :active-panel-right 0]) panel-key)
          (re/dispatch [:layout/hide-panel-right]))
        (assoc-in db selected-conversation-path nil))
      db)))

(re/reg-event-db
  :oasys/move-session re/middleware
  (fn [db [direction]]
    (let [sessions (flattened-list (get-in db list-path))
          idx (get-in db (conj selected-conversation-path :convo-idx))
          next-idx (case direction
                     :next (when (< idx (dec (count sessions))) (inc idx))
                     :prev (when (pos? idx) (dec idx)))
          next-session (some->> next-idx (get sessions))]
      (when (and (= direction :next) next-idx (>= next-idx (dec (count sessions))))
        (re/dispatch [:oasys/load-sessions [nil {:page (next-page db)}]]))
      (if next-session
        (let [conv (-> next-session
                       (select-keys [:conversation-id :conversation-date :conversation-ts])
                       (assoc :convo-idx next-idx))]
          (go-with-merged-conv (dissoc conv :convo-idx))
          (assoc-in db selected-conversation-path conv))
        db))))

(defn sharable-session-url [concept-id conversation]
  (str js/window.location.origin
       (cp.router/route-details->href :oasys/sessions
                                      nil
                                      {:cid concept-id
                                       :conv (conversation->conv-qp conversation)})))

(re/reg-event-fx
  :oasys/sessions-initialize re/middleware
  (fn [{:keys [db]}]
    (re/dispatch [:core/initialize-filters])
    (re/dispatch [:oasys-filters/initialize])
    (re/dispatch [:oasys/reload-sessions])
    {:db (assoc-in db selected-conversation-path nil)
     :forward-events {:register :oasys-sessions-initialization-listener
                      :events #{:concepts/select :core/select-timeframe :core/timeframe-date-range-changed}
                      :dispatch-to [:oasys/reload-sessions]}}))

(re/reg-sub
  :oasys/header-height-
  (fn [db]
    (get-in db header-height-path)))

(re/reg-event-db
  :oasys/set-header-height re/middleware
  (fn [db [h]]
    (assoc-in db header-height-path h)))
